/**
 * ProductDetailsPage — view moderna do produto (V2).
 *
 * Substitui ViewProductPage + ViewProduct (V1 antigo). Consome V2:
 *   productsV2API, physicalProductsV2API, plansV2API, checkoutsV2API,
 *   stockV2API, affiliatesV2API, managersV2API, coproducersV2API, couponsV2API
 *
 * Layout:
 *   - Hero gigante com capa + título + status + ações
 *   - Sidebar lateral fixa (9 seções)
 *   - Conteúdo principal renderizado por seção
 *   - Drawers de edição (carregados sob demanda nas seções)
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import { productsV2API } from '../../services/api/v2/products';
import { physicalProductsV2API } from '../../services/api/v2/physicalProducts';
import { plansV2API } from '../../services/api/v2/plans';
import { checkoutsV2API } from '../../services/api/v2/checkouts';
import { stockV2API } from '../../services/api/v2/stock';
import { affiliatesV2API } from '../../services/api/v2/affiliates';
import { managersV2API } from '../../services/api/v2/managers';
import { coproducersV2API } from '../../services/api/v2/coproducers';
import { couponsV2API } from '../../services/api/v2/coupons';
import { toast } from 'react-toastify';
import { parseApiError } from '../../utils/apiErrors';
import PhysicalProductDrawer from '../../components/products/details/PhysicalProductDrawer';
import PlanDrawer from '../../components/products/details/PlanDrawer';
import StockAdjustDrawer from '../../components/products/details/StockAdjustDrawer';
import CheckoutDrawer from '../../components/products/details/CheckoutDrawer';
import CouponDrawer from '../../components/products/details/CouponDrawer';
import InviteAffiliateDrawer from '../../components/products/details/InviteAffiliateDrawer';
import InviteManagerDrawer from '../../components/products/details/InviteManagerDrawer';
import InviteCoproducerDrawer from '../../components/products/details/InviteCoproducerDrawer';
import OperatorSupplierDrawer from '../../components/products/details/OperatorSupplierDrawer';
import { logisticsRepsV2API } from '../../services/api/v2/logisticsReps';
import { suppliersV2API } from '../../services/api/v2/suppliers';
import './ProductDetailsPage.css';

// ─── Constantes & utilitários ────────────────────────────────────────────

const APPROVAL = {
  approved: { label: 'Aprovado', cls: 'pdetails__hero-pill--approved', icon: 'mdi:check-decagram' },
  pending_approval: { label: 'Em análise', cls: 'pdetails__hero-pill--pending', icon: 'mdi:clock-outline' },
  rejected: { label: 'Reprovado', cls: 'pdetails__hero-pill--rejected', icon: 'mdi:close-circle-outline' },
};

const STATUS = {
  active: { label: 'Ativo' },
  paused: { label: 'Pausado' },
  archived: { label: 'Arquivado' },
};

// IMPORTANTE: valores no banco estão em CENTAVOS. fmtBRL divide por 100
// pra exibir em reais. Não passar reais aqui — passa centavos sempre.
const fmtBRL = (cents) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return '—'; }
};

const isUuid = (str) => typeof str === 'string' && str.includes('-');

// ─── Sidebar de seções ───────────────────────────────────────────────────

const SECTIONS = [
  { id: 'overview',     label: 'Visão geral',     icon: 'mdi:view-dashboard-outline' },
  { id: 'catalog',      label: 'Catálogo',        icon: 'mdi:package-variant' },
  { id: 'plans',        label: 'Planos & Kits',   icon: 'mdi:credit-card-multiple-outline' },
  { id: 'stock',        label: 'Estoque',         icon: 'mdi:warehouse' },
  { id: 'checkouts',    label: 'Checkouts',       icon: 'mdi:cart-outline' },
  { id: 'affiliation',  label: 'Afiliação',       icon: 'mdi:account-multiple-plus-outline' },
  { id: 'team',         label: 'Time',            icon: 'mdi:account-tie-outline' },
  { id: 'operations',   label: 'Operador & Fornecedores', icon: 'mdi:truck-delivery-outline' },
  { id: 'coupons',      label: 'Cupons',          icon: 'mdi:ticket-percent-outline' },
];

// ─── Componente principal ────────────────────────────────────────────────

const ProductDetailsPage = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Estados — lê tab da URL pra abrir seção certa direto (ex: pós-cadastro)
  const initialSection = searchParams.get('tab') || 'overview';
  const showWelcome = searchParams.get('welcome') === '1';
  const [activeSection, setActiveSection] = useState(initialSection);
  const [welcomeVisible, setWelcomeVisible] = useState(showWelcome);

  // Limpa o welcome da URL após ler (visualmente fica, mas remove o param)
  useEffect(() => {
    if (showWelcome) {
      const next = new URLSearchParams(searchParams);
      next.delete('welcome');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [physicals, setPhysicals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [managers, setManagers] = useState([]);
  const [coproducers, setCoproducers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [operators, setOperators] = useState([]);   // logistics_reps por físico
  const [suppliers, setSuppliers] = useState([]);   // fornecedores por físico (slot 1..3)
  const [stockMap, setStockMap] = useState({}); // { [physicalId]: { current_quantity, alert_threshold } }

  // Carga inicial — busca produto por UUID ou ID
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const pRes = isUuid(routeId)
          ? await productsV2API.findByUuid(routeId)
          : await productsV2API.findById(routeId);
        const p = pRes.data?.product || pRes.data?.data || pRes.data;
        if (!alive) return;
        setProduct(p);

        // Carrega filhos em paralelo
        const numericId = p.id;
        const [physRes, planRes, coRes, affRes, mgrRes, cpRes, cpnRes, opRes, supRes] = await Promise.allSettled([
          physicalProductsV2API.listByProduct(numericId),
          plansV2API.listByProduct(numericId),
          checkoutsV2API.listByProduct(numericId),
          affiliatesV2API.listByProduct(numericId),
          managersV2API.listByProduct(numericId),
          coproducersV2API.listByProduct(numericId),
          couponsV2API.listByProduct(numericId),
          logisticsRepsV2API.listByProduct(numericId),
          suppliersV2API.listByProduct(numericId),
        ]);
        if (!alive) return;

        const phys = physRes.status === 'fulfilled'
          ? (physRes.value.data?.physical_products || physRes.value.data?.data || [])
          : [];
        setPhysicals(phys);
        setPlans(planRes.status === 'fulfilled' ? (planRes.value.data?.plans || planRes.value.data?.data || []) : []);
        setCheckouts(coRes.status === 'fulfilled' ? (coRes.value.data?.checkouts || coRes.value.data?.data || []) : []);
        setAffiliates(affRes.status === 'fulfilled' ? (affRes.value.data?.affiliates || affRes.value.data?.links || affRes.value.data?.data || []) : []);
        setManagers(mgrRes.status === 'fulfilled' ? (mgrRes.value.data?.managers || mgrRes.value.data?.links || mgrRes.value.data?.data || []) : []);
        setCoproducers(cpRes.status === 'fulfilled' ? (cpRes.value.data?.coproducers || cpRes.value.data?.links || cpRes.value.data?.data || []) : []);
        setCoupons(cpnRes.status === 'fulfilled' ? (cpnRes.value.data?.coupons || cpnRes.value.data?.data || []) : []);
        setOperators(opRes.status === 'fulfilled' ? (opRes.value.data?.logistics_reps || []) : []);
        setSuppliers(supRes.status === 'fulfilled' ? (supRes.value.data?.suppliers || []) : []);

        // Estoque por físico
        const stockResults = await Promise.allSettled(
          phys
            .filter((pp) => pp.product_type === 'physical')
            .map((pp) => stockV2API.getByPhysicalProduct(pp.id))
        );
        if (!alive) return;
        const newStock = {};
        phys
          .filter((pp) => pp.product_type === 'physical')
          .forEach((pp, idx) => {
            const r = stockResults[idx];
            if (r.status === 'fulfilled') {
              const data = r.value.data?.stock || r.value.data?.data || r.value.data;
              newStock[pp.id] = data;
            }
          });
        setStockMap(newStock);
      } catch (err) {
        if (alive) setError(err.message || 'Erro ao carregar produto');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [routeId]);

  // Reloads pontuais — usados após convidar afiliado/gerente/coprodutor.
  // Mantém a UI fresca sem precisar dar reload completo da página.
  const reloadAffiliates = useCallback(async () => {
    if (!product?.id) return;
    try {
      const res = await affiliatesV2API.listByProduct(product.id);
      setAffiliates(res.data?.affiliates || res.data?.links || res.data?.data || []);
    } catch { /* silencioso — UI vai mostrar o estado anterior */ }
  }, [product?.id]);

  const reloadManagers = useCallback(async () => {
    if (!product?.id) return;
    try {
      const res = await managersV2API.listByProduct(product.id);
      setManagers(res.data?.managers || res.data?.links || res.data?.data || []);
    } catch { /* silencioso */ }
  }, [product?.id]);

  const reloadCoproducers = useCallback(async () => {
    if (!product?.id) return;
    try {
      const res = await coproducersV2API.listByProduct(product.id);
      setCoproducers(res.data?.coproducers || res.data?.links || res.data?.data || []);
    } catch { /* silencioso */ }
  }, [product?.id]);

  const reloadOperations = useCallback(async () => {
    if (!product?.id) return;
    try {
      const [opRes, supRes] = await Promise.all([
        logisticsRepsV2API.listByProduct(product.id),
        suppliersV2API.listByProduct(product.id),
      ]);
      setOperators(opRes.data?.logistics_reps || []);
      setSuppliers(supRes.data?.suppliers || []);
    } catch { /* silencioso */ }
  }, [product?.id]);

  // Counters da sidebar
  const counts = useMemo(() => ({
    catalog: physicals.length,
    plans: plans.length,
    stock: physicals.filter((p) => p.product_type === 'physical').length,
    checkouts: checkouts.length,
    affiliation: affiliates.length,
    team: managers.length + coproducers.length,
    operations: operators.length + suppliers.length,
    coupons: coupons.length,
  }), [physicals, plans, checkouts, affiliates, managers, coproducers, operators, suppliers, coupons]);

  /**
   * Completeness do produto: para sair de "rascunho" e ir pra "ativo" no
   * marketplace, exige no mínimo:
   *  - 1 item no catálogo
   *  - 1 plano com price_cents > 0
   *  - 1 checkout vinculado a algum plano
   *
   * Cada step vira um pending na barra do topo. Quando todos verdes, o
   * produto pode realmente ser publicado/aprovado.
   */
  const completeness = useMemo(() => {
    if (!product) return { ready: true, items: [] };
    const items = [
      {
        id: 'catalog',
        ok: physicals.length > 0,
        label: 'Adicione pelo menos 1 item ao catálogo',
        section: 'catalog',
      },
      {
        id: 'plans',
        ok: plans.some((p) => Number(p.price_cents) > 0 && (p.items || []).length > 0),
        label: 'Crie ao menos 1 plano com itens e preço',
        section: 'plans',
      },
      {
        id: 'checkouts',
        ok: checkouts.length > 0,
        label: 'Configure pelo menos 1 checkout',
        section: 'checkouts',
      },
    ];
    const ready = items.every((i) => i.ok);
    return { ready, items, doneCount: items.filter((i) => i.ok).length, total: items.length };
  }, [product, physicals, plans, checkouts]);

  // Render loading / error
  if (loading) {
    return (
      <MasterLayout>
        <div className="pdetails">
          <div className="pdetails__center">
            <div className="pdetails__spinner" />
            <span>Carregando produto...</span>
          </div>
        </div>
      </MasterLayout>
    );
  }

  if (error || !product) {
    return (
      <MasterLayout>
        <div className="pdetails">
          <div className="pdetails__center">
            <Icon icon="mdi:alert-circle" width="40" style={{ color: '#ef4444' }} />
            <span style={{ color: '#991b1b' }}>{error || 'Produto não encontrado'}</span>
            <button type="button" className="pdetails__action" onClick={() => navigate('/products')}>
              <Icon icon="mdi:arrow-left" width="14" /> Voltar à listagem
            </button>
          </div>
        </div>
      </MasterLayout>
    );
  }

  const approval = APPROVAL[product.approval_status] || APPROVAL.pending_approval;
  const status = STATUS[product.status] || STATUS.active;

  // Hero stats (calculados)
  const priceRange = plans.length
    ? (() => {
        const prices = plans.map((p) => Number(p.price_cents || 0)).filter((v) => v > 0);
        if (!prices.length) return null;
        return { min: Math.min(...prices), max: Math.max(...prices) };
      })()
    : null;

  return (
    <MasterLayout>
      <div className="pdetails">
        <div className="pdetails__wrap">

          {/* Topbar */}
          <div className="pdetails__topbar">
            <button type="button" className="pdetails__back" onClick={() => navigate('/products')}>
              <Icon icon="mdi:arrow-left" width="14" />
              Voltar à listagem
            </button>
            <div className="pdetails__topbar-actions">
              <button
                type="button"
                className="pdetails__action"
                onClick={async () => {
                  const url = window.location.href;
                  const title = `Produto: ${product.name || 'Pagmus'}`;
                  try {
                    if (navigator.share) {
                      await navigator.share({ title, url });
                      return;
                    }
                    await navigator.clipboard.writeText(url);
                    alert('Link copiado pra área de transferência.');
                  } catch (err) {
                    if (err?.name !== 'AbortError') {
                      alert('Não foi possível compartilhar.');
                    }
                  }
                }}
              >
                <Icon icon="mdi:share-variant-outline" width="14" />
                Compartilhar
              </button>
              <button
                type="button"
                className="pdetails__action pdetails__action--primary"
                onClick={() => navigate(`/products/${product.uuid || product.id}/edit`)}
              >
                <Icon icon="mdi:pencil-outline" width="14" />
                Editar Produto
              </button>
            </div>
          </div>

          {/* Hero */}
          <header className="pdetails__hero">
            <div className={`pdetails__hero-cover ${!product.image_url ? 'pdetails__hero-cover--empty' : ''}`}>
              {product.image_url && (
                <img src={product.image_url} alt={product.name} />
              )}
            </div>
            <div className="pdetails__hero-overlay" />
            <div className="pdetails__hero-grid-bg" />

            <div className="pdetails__hero-content">
              <div className="pdetails__hero-badges">
                {!completeness.ready ? (
                  <span className="pdetails__hero-pill pdetails__hero-pill--pending">
                    <Icon icon="mdi:file-document-edit-outline" width="12" /> Rascunho
                  </span>
                ) : (
                  <span className={`pdetails__hero-pill ${approval.cls}`}>
                    <Icon icon={approval.icon} width="12" /> {approval.label}
                  </span>
                )}
                <span className="pdetails__hero-pill pdetails__hero-pill--neutral">
                  <Icon icon="mdi:circle-medium" width="14" /> {status.label}
                </span>
                {product.category && (
                  <span className="pdetails__hero-pill pdetails__hero-pill--neutral">
                    <Icon icon="mdi:folder-outline" width="12" /> {product.category}
                  </span>
                )}
              </div>

              <span className="pdetails__hero-title">{product.name}</span>

              <div className="pdetails__hero-subtitle">
                {product.uuid && (
                  <span className="pdetails__hero-subtitle-item" style={{ fontFamily: 'monospace' }}>
                    <Icon icon="mdi:identifier" width="13" />
                    {String(product.uuid).slice(0, 8)}…
                  </span>
                )}
                <span className="pdetails__hero-subtitle-item">
                  <Icon icon="mdi:calendar-outline" width="13" />
                  Criado em {fmtDate(product.created_at)}
                </span>
              </div>

              <div className="pdetails__hero-stats">
                <div className="pdetails__hero-stat">
                  <span className="pdetails__hero-stat-label">Preço</span>
                  <span className="pdetails__hero-stat-value">
                    {priceRange
                      ? (priceRange.min === priceRange.max
                          ? fmtBRL(priceRange.min)
                          : `de ${fmtBRL(priceRange.min)}`)
                      : '—'}
                  </span>
                </div>
                <div className="pdetails__hero-stat">
                  <span className="pdetails__hero-stat-label">Comissão Afiliado</span>
                  <span className="pdetails__hero-stat-value">
                    {product.commission_value_affiliate
                      ? `${product.commission_value_affiliate}${product.commission_type === 'percentage' ? '%' : ' ¢'}`
                      : '—'}
                  </span>
                </div>
                <div className="pdetails__hero-stat">
                  <span className="pdetails__hero-stat-label">Catálogo</span>
                  <span className="pdetails__hero-stat-value">
                    {physicals.length} {physicals.length === 1 ? 'item' : 'itens'}
                  </span>
                </div>
                <div className="pdetails__hero-stat">
                  <span className="pdetails__hero-stat-label">Planos</span>
                  <span className="pdetails__hero-stat-value">
                    {plans.length} {plans.length === 1 ? 'plano' : 'planos'}
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* Banner de Rascunho (pendências) */}
          {!completeness.ready && !welcomeVisible && (
            <div className="pdetails__draft">
              <div className="pdetails__draft-header">
                <div className="pdetails__draft-icon">
                  <Icon icon="mdi:file-document-edit-outline" width="22" />
                </div>
                <div className="pdetails__draft-title-wrap">
                  <span className="pdetails__draft-title">Produto em rascunho</span>
                  <span className="pdetails__draft-subtitle">
                    {completeness.doneCount} de {completeness.total} passos concluídos. Finalize as pendências pra deixar o produto pronto pra vender.
                  </span>
                </div>
              </div>
              <div className="pdetails__draft-progress">
                <div
                  className="pdetails__draft-progress-bar"
                  style={{ width: `${(completeness.doneCount / completeness.total) * 100}%` }}
                />
              </div>
              <div className="pdetails__draft-items">
                {completeness.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`pdetails__draft-item ${item.ok ? 'is-done' : ''}`}
                    onClick={() => setActiveSection(item.section)}
                  >
                    <Icon
                      icon={item.ok ? 'mdi:check-circle' : 'mdi:circle-outline'}
                      width="18"
                      className="pdetails__draft-item-icon"
                    />
                    <span className="pdetails__draft-item-label">{item.label}</span>
                    {!item.ok && (
                      <Icon icon="mdi:arrow-right" width="14" className="pdetails__draft-item-arrow" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Banner de boas-vindas (pós-cadastro) */}
          {welcomeVisible && (
            <div className="pdetails__welcome">
              <div className="pdetails__welcome-icon">
                <Icon icon="mdi:rocket-launch" width="22" />
              </div>
              <div className="pdetails__welcome-content">
                <span className="pdetails__welcome-title">Produto criado com sucesso!</span>
                <span className="pdetails__welcome-desc">
                  Agora adicione os itens do <b>Catálogo</b> (físicos ou digitais).
                  Em seguida, monte os <b>Planos &amp; Kits</b> combinando esses itens.
                </span>
              </div>
              <button
                type="button"
                className="pdetails__welcome-close"
                onClick={() => setWelcomeVisible(false)}
                aria-label="Fechar"
              >
                <Icon icon="mdi:close" width="16" />
              </button>
            </div>
          )}

          {/* Layout: sidebar + conteúdo */}
          <div className="pdetails__layout">
            <aside className="pdetails__sidebar">
              {SECTIONS.map((s) => {
                const count = counts[s.id];
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`pdetails__nav-item ${activeSection === s.id ? 'is-active' : ''}`}
                    onClick={() => setActiveSection(s.id)}
                  >
                    <Icon icon={s.icon} className="pdetails__nav-icon" width="16" />
                    {s.label}
                    {count !== undefined && count > 0 && (
                      <span className="pdetails__nav-badge">{count}</span>
                    )}
                  </button>
                );
              })}
            </aside>

            <div className="pdetails__content">
              {activeSection === 'overview' && (
                <OverviewSection product={product} physicals={physicals} plans={plans} />
              )}
              {activeSection === 'catalog' && (
                <CatalogSection
                  product={product}
                  physicals={physicals}
                  stockMap={stockMap}
                  onChanged={(newList) => setPhysicals(newList)}
                />
              )}
              {activeSection === 'plans' && (
                <PlansSection
                  product={product}
                  plans={plans}
                  physicals={physicals}
                  onChanged={(newList) => setPlans(newList)}
                />
              )}
              {activeSection === 'stock' && (
                <StockSection
                  physicals={physicals}
                  stockMap={stockMap}
                  onStockChanged={(physicalId, newStock) =>
                    setStockMap((prev) => ({ ...prev, [physicalId]: newStock }))
                  }
                />
              )}
              {activeSection === 'checkouts' && (
                <CheckoutsSection
                  product={product}
                  checkouts={checkouts}
                  plans={plans}
                  onChanged={(newList) => setCheckouts(newList)}
                />
              )}
              {activeSection === 'affiliation' && (
                <AffiliationSection
                  product={product}
                  affiliates={affiliates}
                  onReload={reloadAffiliates}
                  onChanged={(newList) => setAffiliates(newList)}
                />
              )}
              {activeSection === 'team' && (
                <TeamSection
                  product={product}
                  managers={managers}
                  coproducers={coproducers}
                  onReloadManagers={reloadManagers}
                  onReloadCoproducers={reloadCoproducers}
                />
              )}
              {activeSection === 'operations' && (
                <OperationsSection
                  product={product}
                  physicals={physicals}
                  operators={operators}
                  suppliers={suppliers}
                  onReload={reloadOperations}
                />
              )}
              {activeSection === 'coupons' && (
                <CouponsSection
                  product={product}
                  coupons={coupons}
                  plans={plans}
                  onChanged={(newList) => setCoupons(newList)}
                />
              )}
            </div>
          </div>

        </div>
      </div>
    </MasterLayout>
  );
};

// ─── Seção: Visão geral ──────────────────────────────────────────────────

const OverviewSection = ({ product, physicals, plans }) => (
  <>
    <div className="pdetails__section-header">
      <div>
        <span className="pdetails__section-title">Visão geral</span>
        <span className="pdetails__section-subtitle">Resumo das informações principais do produto</span>
      </div>
    </div>

    <div className="pdetails__card">
      <div className="pdetails__card-header">
        <span className="pdetails__card-title">
          <span className="pdetails__card-title-icon">
            <Icon icon="mdi:package-variant" width="18" />
          </span>
          Identidade
        </span>
      </div>
      <div className="pdetails__kvs">
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Nome</span>
          <span className="pdetails__kv-value">{product.name || '—'}</span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Categoria</span>
          <span className="pdetails__kv-value">{product.category || '—'}</span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Identificador</span>
          <span className="pdetails__kv-value pdetails__kv-value--mono">{product.uuid || '—'}</span>
        </div>
        {product.description && (
          <div className="pdetails__kv" style={{ gridColumn: '1 / -1' }}>
            <span className="pdetails__kv-label">Descrição</span>
            <span className="pdetails__kv-value" style={{ whiteSpace: 'pre-line' }}>
              {product.description}
            </span>
          </div>
        )}
      </div>
    </div>

    <div className="pdetails__card">
      <div className="pdetails__card-header">
        <span className="pdetails__card-title">
          <span className="pdetails__card-title-icon">
            <Icon icon="mdi:link-variant" width="18" />
          </span>
          URLs & Suporte
        </span>
      </div>
      <div className="pdetails__kvs">
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Página de Vendas</span>
          <span className="pdetails__kv-value">
            {product.sales_page_url
              ? <a href={product.sales_page_url} target="_blank" rel="noreferrer">{product.sales_page_url}</a>
              : '—'}
          </span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Agradecimento</span>
          <span className="pdetails__kv-value">
            {product.thank_you_page_url
              ? <a href={product.thank_you_page_url} target="_blank" rel="noreferrer">{product.thank_you_page_url}</a>
              : '—'}
          </span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Reclame Aqui</span>
          <span className="pdetails__kv-value">
            {product.complaint_url
              ? <a href={product.complaint_url} target="_blank" rel="noreferrer">{product.complaint_url}</a>
              : '—'}
          </span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Email de Suporte</span>
          <span className="pdetails__kv-value">{product.support_email || '—'}</span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Telefone de Suporte</span>
          <span className="pdetails__kv-value">{product.support_phone || '—'}</span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Garantia</span>
          <span className="pdetails__kv-value">{product.warranty_days || 7} dias</span>
        </div>
      </div>
    </div>

    <div className="pdetails__card">
      <div className="pdetails__card-header">
        <span className="pdetails__card-title">
          <span className="pdetails__card-title-icon" style={{ background: '#fef3c7', color: '#b45309' }}>
            <Icon icon="mdi:chart-line" width="18" />
          </span>
          Resumo
        </span>
      </div>
      <div className="pdetails__kvs pdetails__kvs--3">
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Itens no catálogo</span>
          <span className="pdetails__kv-value">{physicals.length}</span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Planos cadastrados</span>
          <span className="pdetails__kv-value">{plans.length}</span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Tipo de Comissão</span>
          <span className="pdetails__kv-value">
            {product.commission_type === 'percentage' ? 'Porcentagem' : 'Valor fixo'}
          </span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Comissão Afiliado</span>
          <span className="pdetails__kv-value">{product.commission_value_affiliate || 0}%</span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Comissão Gerente</span>
          <span className="pdetails__kv-value">{product.commission_value_manager || 0}%</span>
        </div>
        <div className="pdetails__kv">
          <span className="pdetails__kv-label">Comissão Coprodutor</span>
          <span className="pdetails__kv-value">{product.commission_value_coproducer || 0}%</span>
        </div>
      </div>
    </div>
  </>
);

// ─── Seção: Catálogo (Produtos físicos/digitais) ─────────────────────────

const CatalogSection = ({ product, physicals, stockMap, onChanged }) => {
  const [drawer, setDrawer] = useState({ open: false, item: null });

  const openCreate = () => setDrawer({ open: true, item: null });
  const openEdit = (pp) => setDrawer({ open: true, item: pp });
  const close = () => setDrawer({ open: false, item: null });

  const handleSaved = (saved) => {
    const exists = physicals.some((p) => p.id === saved.id);
    onChanged?.(exists
      ? physicals.map((p) => (p.id === saved.id ? saved : p))
      : [...physicals, saved]);
  };

  const handleRemove = async (pp) => {
    if (!window.confirm(`Remover "${pp.name}" do catálogo? Esta ação não pode ser desfeita.`)) return;
    try {
      await physicalProductsV2API.remove(pp.id);
      onChanged?.(physicals.filter((p) => p.id !== pp.id));
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Erro ao remover item');
    }
  };

  return (
    <>
      <div className="pdetails__section-header">
        <div>
          <span className="pdetails__section-title">Catálogo de itens</span>
          <span className="pdetails__section-subtitle">
            Cada item físico/digital que compõe seu produto. Use-os pra montar Planos &amp; Kits.
          </span>
        </div>
        <button type="button" className="pdetails__action pdetails__action--primary" onClick={openCreate}>
          <Icon icon="mdi:plus" width="14" /> Adicionar item
        </button>
      </div>

      <div className="pdetails__card">
        {physicals.length === 0 ? (
          <EmptyState
            icon="mdi:package-variant-closed"
            title="Nenhum item cadastrado"
            description="Comece adicionando os produtos físicos ou digitais que compõem este produto."
            actionLabel="Adicionar primeiro item"
            onAction={openCreate}
          />
        ) : (
          <div className="pdetails__items">
            {physicals.map((pp) => {
              const stock = stockMap[pp.id];
              const isDigital = pp.product_type === 'digital';
              return (
                <div key={pp.id} className="pdetails__item">
                  <div className={`pdetails__item-icon ${isDigital ? 'pdetails__item-icon--amber' : ''}`}>
                    <Icon icon={isDigital ? 'mdi:cloud-download' : 'mdi:cube-outline'} width="22" />
                  </div>
                  <div className="pdetails__item-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          background: isDigital ? '#fef3c7' : '#dbeafe',
                          color: isDigital ? '#92400e' : '#1e40af',
                        }}
                      >
                        <Icon icon={isDigital ? 'mdi:cloud-download' : 'mdi:cube-outline'} width="11" />
                        {isDigital ? 'Produto Digital' : 'Produto Físico'}
                      </span>
                      <span className="pdetails__item-name">{pp.name || 'Sem nome'}</span>
                    </div>
                    <span className="pdetails__item-meta">
                      {pp.sku && <code>{pp.sku}</code>}
                      {!isDigital && (
                        <span>
                          {pp.height_cm || 0}×{pp.width_cm || 0}×{pp.length_cm || 0}cm
                        </span>
                      )}
                      {!isDigital && (
                        <span>{((pp.weight_grams || 0) / 1000).toFixed(3)}kg</span>
                      )}
                      {!isDigital && stock && (
                        <span>Estoque: {stock.current_quantity ?? 0}</span>
                      )}
                    </span>
                  </div>
                  <div className="pdetails__item-trailing">
                    <span className="pdetails__item-price">{fmtBRL(pp.price_cents)}</span>
                    <div className="pdetails__item-actions">
                      <button type="button" className="pdetails__icon-btn" title="Editar item" onClick={() => openEdit(pp)}>
                        <Icon icon="mdi:pencil-outline" width="14" />
                      </button>
                      <button
                        type="button"
                        className="pdetails__icon-btn pdetails__icon-btn--danger"
                        title="Remover item"
                        onClick={() => handleRemove(pp)}
                      >
                        <Icon icon="mdi:trash-can-outline" width="14" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PhysicalProductDrawer
        isOpen={drawer.open}
        onClose={close}
        productId={product.id}
        physicalProduct={drawer.item}
        onSaved={handleSaved}
      />
    </>
  );
};

// ─── Seção: Planos & Kits ────────────────────────────────────────────────

const PlansSection = ({ product, plans, physicals, onChanged }) => {
  const [drawer, setDrawer] = useState({ open: false, item: null });
  const openCreate = () => setDrawer({ open: true, item: null });
  const openEdit = (p) => setDrawer({ open: true, item: p });
  const close = () => setDrawer({ open: false, item: null });

  const handleSaved = (saved) => {
    const exists = plans.some((p) => p.id === saved.id);
    onChanged?.(exists
      ? plans.map((p) => (p.id === saved.id ? saved : p))
      : [...plans, saved]);
  };

  const handleRemove = async (pl) => {
    if (!window.confirm(`Remover o plano "${pl.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await plansV2API.remove(pl.id);
      onChanged?.(plans.filter((p) => p.id !== pl.id));
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Erro ao remover plano');
    }
  };

  return (
  <>
    <div className="pdetails__section-header">
      <div>
        <span className="pdetails__section-title">Planos &amp; Kits</span>
        <span className="pdetails__section-subtitle">
          Ofertas comerciais. Cada plano é montado a partir dos itens do catálogo.
        </span>
      </div>
      <button
        type="button"
        className="pdetails__action pdetails__action--primary"
        onClick={openCreate}
        disabled={physicals.length === 0}
        title={physicals.length === 0 ? 'Cadastre itens no Catálogo primeiro' : ''}
      >
        <Icon icon="mdi:plus" width="14" /> Novo plano
      </button>
    </div>

    {plans.length === 0 ? (
      <div className="pdetails__card">
        <EmptyState
          icon="mdi:credit-card-multiple-outline"
          title="Nenhum plano configurado"
          description={physicals.length === 0
            ? 'Cadastre itens no Catálogo primeiro pra poder montar planos.'
            : 'Crie um plano simples ou kit combinando itens do catálogo.'}
          actionLabel={physicals.length > 0 ? 'Criar primeiro plano' : null}
          onAction={physicals.length > 0 ? openCreate : null}
        />
      </div>
    ) : (
      plans.map((pl) => {
        const items = pl.items || [];
        const isKit = items.length > 1;
        const subtotal = items.reduce((acc, it) => {
          const phys = physicals.find((p) => p.id === it.physical_product_id);
          return acc + (Number(phys?.price_cents || 0) * Number(it.quantity || 1));
        }, 0);
        const margin = subtotal - Number(pl.price_cents || 0);
        return (
          <article key={pl.id} className="pdetails__plan-card">
            <div className="pdetails__plan-head">
              <div>
                <span className="pdetails__plan-name">
                  {pl.name}
                  <span className={`pdetails__plan-tag ${isKit ? 'pdetails__plan-tag--kit' : 'pdetails__plan-tag--simple'}`}>
                    {isKit ? `Kit · ${items.length} itens` : 'Simples'}
                  </span>
                </span>
                <div className="pdetails__plan-meta" style={{ marginTop: 6 }}>
                  {(pl.payment_methods || []).map((pm) => (
                    <span key={pm} className="pdetails__plan-meta-pill">
                      <Icon icon={
                        pm === 'credit_card' ? 'mdi:credit-card-outline'
                          : pm === 'pix' ? 'mdi:qrcode'
                          : pm === 'boleto' ? 'mdi:barcode'
                          : 'mdi:cash'
                      } width="11" />
                      {pm === 'credit_card' ? 'Cartão' : pm === 'pix' ? 'PIX' : pm === 'boleto' ? 'Boleto' : pm}
                    </span>
                  ))}
                  <span className="pdetails__plan-meta-pill">
                    <Icon icon="mdi:refresh" width="11" />
                    {pl.billing_type === 'recurring' ? 'Recorrente' : 'Única'}
                  </span>
                  <span className="pdetails__plan-meta-pill">
                    <Icon icon="mdi:credit-card-clock-outline" width="11" />
                    Até {pl.installments_max || 1}x
                  </span>
                </div>
              </div>
              <div className="pdetails__item-actions">
                <button type="button" className="pdetails__icon-btn" title="Editar plano" onClick={() => openEdit(pl)}>
                  <Icon icon="mdi:pencil-outline" width="14" />
                </button>
                <button
                  type="button"
                  className="pdetails__icon-btn pdetails__icon-btn--danger"
                  title="Remover plano"
                  onClick={() => handleRemove(pl)}
                >
                  <Icon icon="mdi:trash-can-outline" width="14" />
                </button>
              </div>
            </div>

            <div className="pdetails__plan-pricing">
              <div>
                <span className="pdetails__plan-pricing-item-label">Preço de venda</span>
                <span className="pdetails__plan-pricing-item-value pdetails__plan-pricing-item-value--accent">
                  {fmtBRL(pl.price_cents)}
                </span>
              </div>
              <div>
                <span className="pdetails__plan-pricing-item-label">Subtotal dos itens</span>
                <span className="pdetails__plan-pricing-item-value">
                  {fmtBRL(subtotal)}
                </span>
              </div>
              <div>
                <span className="pdetails__plan-pricing-item-label">Margem aplicada</span>
                <span className={`pdetails__plan-pricing-item-value ${margin > 0 ? 'pdetails__plan-pricing-item-value--success' : margin < 0 ? 'pdetails__plan-pricing-item-value--warning' : ''}`}>
                  {margin === 0 ? 'R$ 0,00' : margin > 0 ? `+${fmtBRL(margin)}` : `-${fmtBRL(Math.abs(margin))}`}
                </span>
              </div>
            </div>

            <div className="pdetails__kit-list">
              <span className="pdetails__kit-list-title">Composição do plano</span>
              {items.length === 0 ? (
                <span style={{ color: '#9ca3af', fontSize: 12 }}>Nenhum item ainda</span>
              ) : (
                items.map((it, idx) => {
                  const phys = physicals.find((p) => p.id === it.physical_product_id);
                  const itemSubtotal = Number(phys?.price_cents || 0) * Number(it.quantity || 1);
                  return (
                    <div key={`${pl.id}-${idx}`} className="pdetails__kit-item">
                      <span className="pdetails__kit-qty">{it.quantity}×</span>
                      <Icon
                        icon={phys?.product_type === 'digital' ? 'mdi:cloud-download' : 'mdi:cube-outline'}
                        width="16"
                        style={{ color: '#6b7280' }}
                      />
                      <span className="pdetails__kit-name">
                        {phys?.name || `Item #${it.physical_product_id}`}
                      </span>
                      <span className="pdetails__kit-subtotal">{fmtBRL(itemSubtotal)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </article>
        );
      })
    )}

    <PlanDrawer
      isOpen={drawer.open}
      onClose={close}
      productId={product.id}
      physicalProducts={physicals}
      plan={drawer.item}
      onSaved={handleSaved}
    />
  </>
  );
};

// ─── Seção: Estoque ──────────────────────────────────────────────────────

const StockSection = ({ physicals, stockMap, onStockChanged }) => {
  const [drawer, setDrawer] = useState({ open: false, item: null });
  const openAdjust = (pp) => setDrawer({ open: true, item: pp });
  const close = () => setDrawer({ open: false, item: null });

  const handleSaved = (newStock) => {
    if (drawer.item) {
      onStockChanged?.(drawer.item.id, newStock);
    }
  };

  const physicalItems = physicals.filter((p) => p.product_type === 'physical');
  return (
    <>
      <div className="pdetails__section-header">
        <div>
          <span className="pdetails__section-title">Estoque</span>
          <span className="pdetails__section-subtitle">
            Controle de saldo por produto físico. Itens digitais não têm estoque.
          </span>
        </div>
      </div>

      <div className="pdetails__card">
        {physicalItems.length === 0 ? (
          <EmptyState
            icon="mdi:warehouse"
            title="Nenhum item físico"
            description="Adicione produtos físicos no catálogo pra controlar estoque."
          />
        ) : (
          <div className="pdetails__items">
            {physicalItems.map((pp) => {
              const stock = stockMap[pp.id];
              const qty = stock?.current_quantity ?? 0;
              const threshold = stock?.alert_threshold ?? 50;
              const lowStock = qty <= threshold;
              return (
                <div key={pp.id} className="pdetails__item">
                  <div className={`pdetails__item-icon ${lowStock ? 'pdetails__item-icon--amber' : 'pdetails__item-icon--green'}`}>
                    <Icon icon={lowStock ? 'mdi:alert-circle-outline' : 'mdi:check-circle-outline'} width="22" />
                  </div>
                  <div className="pdetails__item-body">
                    <span className="pdetails__item-name">{pp.name}</span>
                    <span className="pdetails__item-meta">
                      {pp.sku && <code>{pp.sku}</code>}
                      <span>Alerta em: {threshold} un</span>
                      {lowStock && <span style={{ color: '#b45309', fontWeight: 700 }}>⚠ Estoque baixo</span>}
                    </span>
                  </div>
                  <div className="pdetails__item-trailing">
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Atual</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: lowStock ? '#b45309' : '#047857' }}>{qty}</div>
                    </div>
                    <div className="pdetails__item-actions">
                      <button type="button" className="pdetails__btn pdetails__btn--primary" onClick={() => openAdjust(pp)}>
                        <Icon icon="mdi:plus-minus" width="14" />
                        Ajustar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <StockAdjustDrawer
        isOpen={drawer.open}
        onClose={close}
        physicalProduct={drawer.item}
        currentStock={drawer.item ? stockMap[drawer.item.id] : null}
        onSaved={handleSaved}
      />
    </>
  );
};

// ─── Seção: Checkouts ────────────────────────────────────────────────────

const CheckoutsSection = ({ product, checkouts, plans, onChanged }) => {
  const [drawer, setDrawer] = useState({ open: false, item: null });
  const openCreate = () => setDrawer({ open: true, item: null });
  const openEdit = (co) => setDrawer({ open: true, item: co });
  const close = () => setDrawer({ open: false, item: null });

  const handleSaved = (saved) => {
    const exists = checkouts.some((c) => c.id === saved.id);
    onChanged?.(exists
      ? checkouts.map((c) => (c.id === saved.id ? saved : c))
      : [...checkouts, saved]);
  };

  const handleRemove = async (co) => {
    if (!window.confirm(`Remover o checkout "${co.name}"?`)) return;
    try {
      await checkoutsV2API.remove(co.id);
      onChanged?.(checkouts.filter((c) => c.id !== co.id));
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Erro ao remover checkout');
    }
  };

  return (
  <>
    <div className="pdetails__section-header">
      <div>
        <span className="pdetails__section-title">Checkouts</span>
        <span className="pdetails__section-subtitle">URLs personalizadas pra cada oferta.</span>
      </div>
      <button
        type="button"
        className="pdetails__action pdetails__action--primary"
        onClick={openCreate}
        disabled={plans.length === 0}
        title={plans.length === 0 ? 'Crie planos primeiro' : ''}
      >
        <Icon icon="mdi:plus" width="14" /> Novo checkout
      </button>
    </div>

    <div className="pdetails__card">
      {checkouts.length === 0 ? (
        <EmptyState
          icon="mdi:cart-outline"
          title="Nenhum checkout criado"
          description={plans.length === 0
            ? 'Crie um plano primeiro pra poder criar checkouts vinculados.'
            : 'Configure URLs de checkout vinculadas aos seus planos.'}
          actionLabel={plans.length > 0 ? 'Criar primeiro checkout' : null}
          onAction={plans.length > 0 ? openCreate : null}
        />
      ) : (
        <div className="pdetails__items">
          {checkouts.map((co) => {
            const plan = plans.find((p) => p.id === co.plan_id);
            return (
              <div key={co.id} className="pdetails__item">
                <div className="pdetails__item-icon">
                  <Icon icon="mdi:cart-outline" width="22" />
                </div>
                <div className="pdetails__item-body">
                  <span className="pdetails__item-name">{co.name}</span>
                  <span className="pdetails__item-meta">
                    {co.slug && <code>/{co.slug}</code>}
                    {plan && <span>Plano: {plan.name}</span>}
                  </span>
                </div>
                <div className="pdetails__item-actions">
                  <button type="button" className="pdetails__icon-btn" title="Editar" onClick={() => openEdit(co)}>
                    <Icon icon="mdi:pencil-outline" width="14" />
                  </button>
                  <button
                    type="button"
                    className="pdetails__icon-btn pdetails__icon-btn--danger"
                    title="Remover"
                    onClick={() => handleRemove(co)}
                  >
                    <Icon icon="mdi:trash-can-outline" width="14" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    <CheckoutDrawer
      isOpen={drawer.open}
      onClose={close}
      productId={product.id}
      plans={plans}
      checkout={drawer.item}
      onSaved={handleSaved}
    />
  </>
  );
};

// ─── Seção: Afiliação ────────────────────────────────────────────────────

const AffiliationSection = ({ product, affiliates, onReload }) => {
  const [showInvite, setShowInvite] = useState(false);

  return (
    <>
      <div className="pdetails__section-header">
        <div>
          <span className="pdetails__section-title">Afiliação</span>
          <span className="pdetails__section-subtitle">Afiliados vinculados ao produto.</span>
        </div>
        <button
          type="button"
          className="pdetails__action pdetails__action--primary"
          onClick={() => setShowInvite(true)}
        >
          <Icon icon="mdi:account-plus" width="14" /> Convidar afiliado
        </button>
      </div>

      <div className="pdetails__card">
        {affiliates.length === 0 ? (
          <EmptyState
            icon="mdi:account-multiple-plus-outline"
            title="Nenhum afiliado ainda"
            description="Convide afiliados pra promoverem seu produto."
            actionLabel="Convidar afiliado"
            onAction={() => setShowInvite(true)}
          />
        ) : (
          <div className="pdetails__items">
            {affiliates.map((a) => (
              <div key={a.id} className="pdetails__item">
                <div className="pdetails__item-icon">
                  <Icon icon="mdi:account-tie-outline" width="22" />
                </div>
                <div className="pdetails__item-body">
                  <span className="pdetails__item-name">{a.user_name || `User #${a.user_id}`}</span>
                  <span className="pdetails__item-meta">
                    <span>Status: {a.status}</span>
                    {a.affiliate_code && <code>{a.affiliate_code}</code>}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <InviteAffiliateDrawer
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        productId={product?.id}
        onSaved={() => onReload?.()}
      />
    </>
  );
};

// ─── Seção: Time (Gerentes + Coprodutores) ───────────────────────────────

const TeamSection = ({ product, managers, coproducers, onReloadManagers, onReloadCoproducers }) => {
  const [showInviteManager, setShowInviteManager] = useState(false);
  const [showInviteCoproducer, setShowInviteCoproducer] = useState(false);

  return (
    <>
      <div className="pdetails__section-header">
        <div>
          <span className="pdetails__section-title">Time do produto</span>
          <span className="pdetails__section-subtitle">Gerentes e coprodutores vinculados.</span>
        </div>
      </div>

      <div className="pdetails__card">
        <div className="pdetails__card-header">
          <span className="pdetails__card-title">
            <span className="pdetails__card-title-icon">
              <Icon icon="mdi:account-tie-outline" width="18" />
            </span>
            Gerentes ({managers.length})
          </span>
          <button
            type="button"
            className="pdetails__btn pdetails__btn--primary"
            onClick={() => setShowInviteManager(true)}
          >
            <Icon icon="mdi:plus" width="14" /> Convidar gerente
          </button>
        </div>
        {managers.length === 0 ? (
          <span style={{ color: '#6b7280', fontSize: 13 }}>Nenhum gerente cadastrado.</span>
        ) : (
          <div className="pdetails__items">
            {managers.map((m) => (
              <div key={m.id} className="pdetails__item">
                <div className="pdetails__item-icon"><Icon icon="mdi:account-tie" width="22" /></div>
                <div className="pdetails__item-body">
                  <span className="pdetails__item-name">{m.user_name || `User #${m.user_id}`}</span>
                  <span className="pdetails__item-meta">
                    Status: {m.status} {m.can_manage_affiliates && '· Gerencia afiliados'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pdetails__card">
        <div className="pdetails__card-header">
          <span className="pdetails__card-title">
            <span className="pdetails__card-title-icon" style={{ background: '#fef3c7', color: '#b45309' }}>
              <Icon icon="mdi:account-supervisor-circle-outline" width="18" />
            </span>
            Coprodutores ({coproducers.length})
          </span>
          <button
            type="button"
            className="pdetails__btn pdetails__btn--primary"
            onClick={() => setShowInviteCoproducer(true)}
          >
            <Icon icon="mdi:plus" width="14" /> Convidar coprodutor
          </button>
        </div>
        {coproducers.length === 0 ? (
          <span style={{ color: '#6b7280', fontSize: 13 }}>Nenhum coprodutor cadastrado.</span>
        ) : (
          <div className="pdetails__items">
            {coproducers.map((c) => (
              <div key={c.id} className="pdetails__item">
                <div className="pdetails__item-icon pdetails__item-icon--amber"><Icon icon="mdi:account-supervisor-circle" width="22" /></div>
                <div className="pdetails__item-body">
                  <span className="pdetails__item-name">{c.user_name || `User #${c.user_id}`}</span>
                  <span className="pdetails__item-meta">Status: {c.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <InviteManagerDrawer
        isOpen={showInviteManager}
        onClose={() => setShowInviteManager(false)}
        productId={product?.id}
        onSaved={() => onReloadManagers?.()}
      />
      <InviteCoproducerDrawer
        isOpen={showInviteCoproducer}
        onClose={() => setShowInviteCoproducer(false)}
        productId={product?.id}
        onSaved={() => onReloadCoproducers?.()}
      />
    </>
  );
};

// ─── Seção: Operador & Fornecedores ──────────────────────────────────────

const centsToBRL = (cents) =>
  Number(cents || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const OperationsSection = ({ product, physicals, operators, suppliers, onReload }) => {
  const [drawer, setDrawer] = useState({
    open: false, kind: null, mode: 'invite',
    physicalProduct: null, supplierPosition: null, current: null,
  });

  const openInviteOperator = (pp) => setDrawer({
    open: true, kind: 'operator', mode: 'invite',
    physicalProduct: pp, supplierPosition: null, current: null,
  });
  const openEditOperator = (pp, current) => setDrawer({
    open: true, kind: 'operator', mode: 'edit',
    physicalProduct: pp, supplierPosition: null, current,
  });
  const openInviteSupplier = (pp, position) => setDrawer({
    open: true, kind: 'supplier', mode: 'invite',
    physicalProduct: pp, supplierPosition: position, current: null,
  });
  const openEditSupplier = (pp, position, current) => setDrawer({
    open: true, kind: 'supplier', mode: 'edit',
    physicalProduct: pp, supplierPosition: position, current,
  });
  const close = () => setDrawer((d) => ({ ...d, open: false }));

  const removeOperator = async (pp) => {
    if (!window.confirm(`Remover Operador Logístico de "${pp.name}"?`)) return;
    try {
      await logisticsRepsV2API.remove(pp.id);
      toast.success('Operador removido');
      onReload?.();
    } catch (err) {
      const { message } = parseApiError(err, 'Erro ao remover operador');
      toast.error(message);
    }
  };

  const removeSupplier = async (pp, position) => {
    if (!window.confirm(`Remover Fornecedor (slot ${position}) de "${pp.name}"?`)) return;
    try {
      await suppliersV2API.removeSlot(pp.id, position);
      toast.success('Fornecedor removido');
      onReload?.();
    } catch (err) {
      const { message } = parseApiError(err, 'Erro ao remover fornecedor');
      toast.error(message);
    }
  };

  // Agrupa por physical_product_id
  const operatorByPP = new Map(operators.map((o) => [o.physical_product_id, o]));
  const suppliersByPP = new Map();
  for (const sup of suppliers) {
    const list = suppliersByPP.get(sup.physical_product_id) || [];
    list.push(sup);
    suppliersByPP.set(sup.physical_product_id, list);
  }

  return (
    <>
      <div className="pdetails__section-header">
        <div>
          <span className="pdetails__section-title">Operador Logístico & Fornecedores</span>
          <span className="pdetails__section-subtitle">
            Valores fixos por item do catálogo, descontados antes das comissões %.
          </span>
        </div>
      </div>

      {physicals.length === 0 && (
        <div className="pdetails__card">
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            Cadastre itens no catálogo primeiro pra atribuir Operador e Fornecedores.
          </span>
        </div>
      )}

      {physicals.map((pp) => {
        const op = operatorByPP.get(pp.id);
        const sups = (suppliersByPP.get(pp.id) || []).sort((a, b) => a.supplier_position - b.supplier_position);
        const supSlots = [1, 2, 3].map((pos) => sups.find((s) => s.supplier_position === pos) || null);

        return (
          <div key={pp.id} className="pdetails__card">
            <div className="pdetails__card-header">
              <span className="pdetails__card-title">
                <span className="pdetails__card-title-icon">
                  <Icon icon="mdi:package-variant" width="18" />
                </span>
                {pp.name}
              </span>
            </div>

            {/* Operador Logístico */}
            <div className="pdetails__items">
              <div className="pdetails__item">
                <div className="pdetails__item-icon">
                  <Icon icon="mdi:truck-delivery-outline" width="22" />
                </div>
                <div className="pdetails__item-body">
                  <span className="pdetails__item-name">
                    Operador Logístico
                  </span>
                  <span className="pdetails__item-meta">
                    {op ? (
                      <>
                        <span>{op.rep_name || `User #${op.rep_user_id}`}</span>
                        <span>R$ {centsToBRL(op.fixed_amount_cents)}</span>
                      </>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>Não cadastrado</span>
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {op ? (
                    <>
                      <button
                        type="button"
                        className="pdetails__btn"
                        onClick={() => openEditOperator(pp, op)}
                      >
                        <Icon icon="mdi:pencil-outline" width="14" /> Editar
                      </button>
                      <button
                        type="button"
                        className="pdetails__btn"
                        onClick={() => removeOperator(pp)}
                        style={{ color: '#dc2626' }}
                      >
                        <Icon icon="mdi:trash-can-outline" width="14" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="pdetails__btn pdetails__btn--primary"
                      onClick={() => openInviteOperator(pp)}
                    >
                      <Icon icon="mdi:plus" width="14" /> Convidar
                    </button>
                  )}
                </div>
              </div>

              {/* Fornecedores slots 1..3 */}
              {supSlots.map((sup, idx) => {
                const position = idx + 1;
                return (
                  <div key={`sup-${pp.id}-${position}`} className="pdetails__item">
                    <div className="pdetails__item-icon pdetails__item-icon--amber">
                      <Icon icon="mdi:factory" width="22" />
                    </div>
                    <div className="pdetails__item-body">
                      <span className="pdetails__item-name">Fornecedor {position}</span>
                      <span className="pdetails__item-meta">
                        {sup ? (
                          <>
                            <span>{sup.supplier_name || `User #${sup.user_id}`}</span>
                            <span>R$ {centsToBRL(sup.fixed_amount_cents)}</span>
                          </>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>Slot livre</span>
                        )}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {sup ? (
                        <>
                          <button
                            type="button"
                            className="pdetails__btn"
                            onClick={() => openEditSupplier(pp, position, sup)}
                          >
                            <Icon icon="mdi:pencil-outline" width="14" /> Editar
                          </button>
                          <button
                            type="button"
                            className="pdetails__btn"
                            onClick={() => removeSupplier(pp, position)}
                            style={{ color: '#dc2626' }}
                          >
                            <Icon icon="mdi:trash-can-outline" width="14" />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="pdetails__btn pdetails__btn--primary"
                          onClick={() => openInviteSupplier(pp, position)}
                        >
                          <Icon icon="mdi:plus" width="14" /> Convidar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <OperatorSupplierDrawer
        isOpen={drawer.open}
        onClose={close}
        kind={drawer.kind}
        mode={drawer.mode}
        physicalProduct={drawer.physicalProduct}
        supplierPosition={drawer.supplierPosition}
        current={drawer.current}
        onSaved={onReload}
      />
    </>
  );
};

// ─── Seção: Cupons ───────────────────────────────────────────────────────

const CouponsSection = ({ product, coupons, plans, onChanged }) => {
  const [drawer, setDrawer] = useState({ open: false, item: null });
  const openCreate = () => setDrawer({ open: true, item: null });
  const openEdit = (c) => setDrawer({ open: true, item: c });
  const close = () => setDrawer({ open: false, item: null });

  const handleSaved = (saved) => {
    const exists = coupons.some((c) => c.id === saved.id);
    onChanged?.(exists
      ? coupons.map((c) => (c.id === saved.id ? saved : c))
      : [...coupons, saved]);
  };

  const handleRemove = async (c) => {
    if (!window.confirm(`Remover o cupom "${c.code}"?`)) return;
    try {
      await couponsV2API.remove(c.id);
      onChanged?.(coupons.filter((x) => x.id !== c.id));
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Erro ao remover cupom');
    }
  };

  return (
  <>
    <div className="pdetails__section-header">
      <div>
        <span className="pdetails__section-title">Cupons de desconto</span>
        <span className="pdetails__section-subtitle">Códigos promocionais aplicáveis no checkout.</span>
      </div>
      <button type="button" className="pdetails__action pdetails__action--primary" onClick={openCreate}>
        <Icon icon="mdi:plus" width="14" /> Novo cupom
      </button>
    </div>

    <div className="pdetails__card">
      {coupons.length === 0 ? (
        <EmptyState
          icon="mdi:ticket-percent-outline"
          title="Nenhum cupom criado"
          description="Crie códigos de desconto pra suas campanhas."
          actionLabel="Criar primeiro cupom"
          onAction={openCreate}
        />
      ) : (
        <div className="pdetails__items">
          {coupons.map((c) => (
            <div key={c.id} className="pdetails__item">
              <div className="pdetails__item-icon pdetails__item-icon--green">
                <Icon icon="mdi:ticket-percent" width="22" />
              </div>
              <div className="pdetails__item-body">
                <span className="pdetails__item-name">
                  <code>{c.code}</code>
                </span>
                <span className="pdetails__item-meta">
                  <span>
                    {c.discount_type === 'percentage'
                      ? `${Number(c.discount_percent)}% off`
                      : `${fmtBRL(c.discount_cents)} off`}
                  </span>
                  {c.max_uses && <span>Limite: {c.max_uses}</span>}
                  {c.valid_until && <span>Até {fmtDate(c.valid_until)}</span>}
                </span>
              </div>
              <div className="pdetails__item-actions">
                <button type="button" className="pdetails__icon-btn" title="Editar" onClick={() => openEdit(c)}>
                  <Icon icon="mdi:pencil-outline" width="14" />
                </button>
                <button type="button" className="pdetails__icon-btn pdetails__icon-btn--danger" title="Remover" onClick={() => handleRemove(c)}>
                  <Icon icon="mdi:trash-can-outline" width="14" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    <CouponDrawer
      isOpen={drawer.open}
      onClose={close}
      productId={product.id}
      plans={plans}
      coupon={drawer.item}
      onSaved={handleSaved}
    />
  </>
  );
};

// ─── EmptyState reusável ────────────────────────────────────────────────

const EmptyState = ({ icon, title, description, actionLabel, onAction }) => (
  <div className="pdetails__empty-state">
    <div className="pdetails__empty-state-icon">
      <Icon icon={icon} width="28" />
    </div>
    <span className="pdetails__empty-state-title">{title}</span>
    <p className="pdetails__empty-state-desc">{description}</p>
    {actionLabel && (
      <button type="button" className="pdetails__btn pdetails__btn--primary" onClick={onAction}>
        <Icon icon="mdi:plus" width="14" />
        {actionLabel}
      </button>
    )}
  </div>
);

export default ProductDetailsPage;
